import React, { useMemo, useState } from "react";
import {
    Users,
    UserPlus,
    UserCheck,
    Clock,
    CalendarRange,
    CheckCircle2,
    AlertTriangle,
    ClipboardList,
    TrendingUp,
    BarChart3,
    Download,
    FileSpreadsheet,
    LayoutDashboard,
    X,
    Star,
    Target,
    BookOpen,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    LineChart,
    Line,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    AreaChart,
    Area,
} from "recharts";
import "../DashboardStyles/DashboardOnboarding.css";

// ── Charte couleurs ───────────────────────────────────────────────────────────
const COLORS = {
    primary: "#204385",
    secondary: "#ee645f",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#2196f3",
    grey: "#757575",
    purple: "#7c3aed",
    teal: "#0d9488",
};

const CHART_COLORS = ["#204385", "#ee645f", "#4caf50", "#ff9800", "#2196f3", "#7c3aed", "#0d9488"];

// ── Mock data statique ────────────────────────────────────────────────────────
const mockData = {
    kpis: {
        totalNewHires: 34,
        onboardingInProgress: 12,
        completedOnboarding: 18,
        dropouts: 4,
        avgCompletionDays: 21,
        satisfactionRate: 87,
        completionRate: 82,
        mentorAssigned: 28,
    },

    onboardingByDepartment: [
        { name: "IT", value: 10 },
        { name: "Commercial", value: 8 },
        { name: "Finance", value: 5 },
        { name: "RH", value: 4 },
        { name: "Marketing", value: 4 },
        { name: "Logistique", value: 3 },
    ],

    onboardingByStatus: [
        { name: "Terminé", value: 18, color: COLORS.success },
        { name: "En cours", value: 12, color: COLORS.info },
        { name: "Abandonné", value: 4, color: COLORS.error },
    ],

    monthlyTrend: [
        { month: "Jan", recrues: 4, termines: 3 },
        { month: "Fév", recrues: 6, termines: 5 },
        { month: "Mar", recrues: 5, termines: 4 },
        { month: "Avr", recrues: 3, termines: 3 },
        { month: "Mai", recrues: 7, termines: 5 },
        { month: "Juin", recrues: 4, termines: 3 },
        { month: "Juil", recrues: 2, termines: 1 },
        { month: "Aoû", recrues: 1, termines: 1 },
        { month: "Sep", recrues: 2, termines: 0 },
    ],

    stepCompletion: [
        { step: "Documents admin.", taux: 95 },
        { step: "Formation sécurité", taux: 88 },
        { step: "Visite locaux", taux: 82 },
        { step: "Config. poste", taux: 78 },
        { step: "Rencontre équipe", taux: 74 },
        { step: "Formation métier", taux: 65 },
        { step: "Évaluation initiale", taux: 58 },
    ],

    satisfactionByCategory: [
        { category: "Accueil", score: 4.5 },
        { category: "Formation", score: 4.1 },
        { category: "Accompagnement", score: 4.3 },
        { category: "Outils", score: 3.8 },
        { category: "Intégration équipe", score: 4.6 },
    ],

    recentOnboardings: [
        { name: "Yassine El Amrani", department: "IT", startDate: "2026-06-15", progress: 85, status: "En cours" },
        { name: "Salma Benkirane", department: "Commercial", startDate: "2026-06-10", progress: 100, status: "Terminé" },
        { name: "Omar Tazi", department: "Finance", startDate: "2026-06-20", progress: 45, status: "En cours" },
        { name: "Nadia Fassi", department: "Marketing", startDate: "2026-06-01", progress: 100, status: "Terminé" },
        { name: "Rachid Alaoui", department: "IT", startDate: "2026-07-01", progress: 20, status: "En cours" },
    ],
};

// ── Config onglets ────────────────────────────────────────────────────────────
const TABS = [
    { id: "dashboard", label: "Tableau de bord", icon: <LayoutDashboard size={16} /> },
    { id: "etats", label: "États", icon: <FileSpreadsheet size={16} /> },
];

const STATIC_EXCEL_STATES = [
    {
        id: "etat-nouvelles-recrues",
        title: "État des nouvelles recrues",
        description: "Liste complète des collaborateurs en cours d'intégration avec leur progression et département.",
        color: COLORS.primary,
    },
    {
        id: "etat-satisfaction",
        title: "État de satisfaction onboarding",
        description: "Résultats des enquêtes de satisfaction par catégorie d'évaluation.",
        color: COLORS.info,
    },
    {
        id: "etat-synthese",
        title: "Synthèse globale onboarding",
        description: "Rapport consolidé avec KPIs, tendances mensuelles et indicateurs de performance.",
        color: COLORS.secondary,
    },
];

// ── Sous-composants ───────────────────────────────────────────────────────────
function StatCard({ title, value, subtitle, icon, color, trend }) {
    const trendIsPlus = typeof trend === "string" && trend.trim().startsWith("+");
    return (
        <div className="doStatCard">
            <div className="doStatCard__row">
                <div className="doStatCard__left">
                    <div className="doStatCard__title">{title}</div>
                    <div className="doStatCard__value" style={{ color: color || COLORS.primary }}>
                        {value}
                    </div>
                    {subtitle ? <div className="doStatCard__sub">{subtitle}</div> : null}
                    {trend ? (
                        <span className={`doTrend ${trendIsPlus ? "is-plus" : "is-minus"}`}>{trend}</span>
                    ) : null}
                </div>
                <div
                    className="doStatIcon"
                    style={{
                        color: color || COLORS.primary,
                        background: `${color || COLORS.primary}15`,
                        borderColor: `${color || COLORS.primary}25`,
                    }}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="doChartCard">
            <div className="doChartCard__title">{title}</div>
            <div className="doChartCard__body">{children}</div>
        </div>
    );
}

function SectionTitle({ icon, children }) {
    return (
        <div className="doSectionTitle">
            <span className="doSectionTitle__icon">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function DateFilterFields({ dateDebut, dateFin, onChangeDebut, onChangeFin }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: "6px 12px",
            }}
        >
            <CalendarRange size={15} color={COLORS.grey} />
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label htmlFor="do-date-debut" style={{ fontSize: 12, color: COLORS.grey }}>Du</label>
                <input
                    id="do-date-debut"
                    type="date"
                    value={dateDebut}
                    onChange={(e) => onChangeDebut(e.target.value)}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "4px 8px", fontSize: 13, color: "#333" }}
                />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label htmlFor="do-date-fin" style={{ fontSize: 12, color: COLORS.grey }}>Au</label>
                <input
                    id="do-date-fin"
                    type="date"
                    value={dateFin}
                    onChange={(e) => onChangeFin(e.target.value)}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "4px 8px", fontSize: 13, color: "#333" }}
                />
            </div>
            {(dateDebut || dateFin) && (
                <button
                    type="button"
                    onClick={() => { onChangeDebut(""); onChangeFin(""); }}
                    style={{
                        background: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "4px 8px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: COLORS.secondary,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}
                    title="Réinitialiser les filtres"
                >
                    <X size={12} />
                    Effacer
                </button>
            )}
        </div>
    );
}

function ExcelStateCard({ title, description, color, onDownload }) {
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: 18,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                    style={{
                        width: 40, height: 40, minWidth: 40,
                        borderRadius: 10,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color,
                        background: `${color}15`,
                        border: `1px solid ${color}25`,
                    }}
                >
                    <FileSpreadsheet size={20} />
                </div>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#1f2937" }}>{title}</div>
                    <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 4, lineHeight: 1.4 }}>{description}</div>
                </div>
            </div>
            <div
                style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    paddingTop: 8, borderTop: "1px dashed #eef0f2",
                }}
            >
                <span style={{ fontSize: 11.5, color: "#9ca3af", display: "flex", alignItems: "center", gap: 5 }}>
                    <Clock size={12} /> Mis à jour à la demande
                </span>
                <button
                    type="button"
                    onClick={onDownload}
                    style={{
                        display: "flex", alignItems: "center", gap: 6,
                        border: "none", borderRadius: 8, padding: "7px 12px",
                        fontSize: 12.5, fontWeight: 600, color: "#fff",
                        background: color, cursor: "pointer",
                    }}
                >
                    <Download size={14} />
                    Télécharger
                </button>
            </div>
        </div>
    );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function DashboardOnboarding() {
    const [data] = useState(mockData);
    const [activeTab, setActiveTab] = useState("dashboard");
    const [dateDebut, setDateDebut] = useState("");
    const [dateFin, setDateFin] = useState("");

    const lastUpdated = useMemo(() => new Date().toLocaleString("fr-FR"), []);

    // ── Générateurs Excel statiques ──────────────────────────────────────────
    const handleDownloadRecrues = () => {
        const rows = data.recentOnboardings.map((r) => ({
            Collaborateur: r.name,
            Département: r.department,
            "Date d'entrée": r.startDate,
            "Progression (%)": r.progress,
            Statut: r.status,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Nouvelles recrues");
        XLSX.writeFile(wb, "Etat_Nouvelles_Recrues.xlsx");
    };

    const handleDownloadProgression = () => {
        const rows = data.stepCompletion.map((s) => ({
            Étape: s.step,
            "Taux de complétion (%)": s.taux,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Progression");
        XLSX.writeFile(wb, "Etat_Progression_Etapes.xlsx");
    };

    const handleDownloadSatisfaction = () => {
        const rows = data.satisfactionByCategory.map((s) => ({
            Catégorie: s.category,
            "Score (/5)": s.score,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Satisfaction");
        XLSX.writeFile(wb, "Etat_Satisfaction_Onboarding.xlsx");
    };

    const handleDownloadSynthese = () => {
        const kpiRows = [
            { Indicateur: "Nouvelles recrues", Valeur: data.kpis.totalNewHires },
            { Indicateur: "Onboarding en cours", Valeur: data.kpis.onboardingInProgress },
            { Indicateur: "Onboarding terminés", Valeur: data.kpis.completedOnboarding },
            { Indicateur: "Abandons", Valeur: data.kpis.dropouts },
            { Indicateur: "Durée moyenne (jours)", Valeur: data.kpis.avgCompletionDays },
            { Indicateur: "Taux de satisfaction (%)", Valeur: data.kpis.satisfactionRate },
            { Indicateur: "Taux de complétion (%)", Valeur: data.kpis.completionRate },
            { Indicateur: "Parrains assignés", Valeur: data.kpis.mentorAssigned },
        ];
        const wsKpi = XLSX.utils.json_to_sheet(kpiRows);
        const wsTrend = XLSX.utils.json_to_sheet(
            data.monthlyTrend.map((t) => ({ Mois: t.month, "Recrues": t.recrues, "Terminés": t.termines }))
        );
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsKpi, "KPIs");
        XLSX.utils.book_append_sheet(wb, wsTrend, "Tendance mensuelle");
        XLSX.writeFile(wb, "Synthese_Onboarding.xlsx");
    };

    const downloadHandlers = {
        "etat-nouvelles-recrues": handleDownloadRecrues,
        "etat-progression": handleDownloadProgression,
        "etat-satisfaction": handleDownloadSatisfaction,
        "etat-synthese": handleDownloadSynthese,
    };

    return (
        <div className="doPage">
            {/* Header */}
            <header className="doHeader">
                <div />
                <DateFilterFields
                    dateDebut={dateDebut}
                    dateFin={dateFin}
                    onChangeDebut={setDateDebut}
                    onChangeFin={setDateFin}
                />
            </header>

            {/* Navigation onglets */}
            <div
                style={{
                    display: "flex",
                    gap: 4,
                    padding: "0 32px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#fff",
                }}
            >
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            padding: "12px 18px",
                            border: "none",
                            borderBottom: activeTab === tab.id ? `2px solid ${COLORS.primary}` : "2px solid transparent",
                            background: "transparent",
                            fontWeight: activeTab === tab.id ? 700 : 500,
                            color: activeTab === tab.id ? COLORS.primary : "#6b7280",
                            fontSize: 14,
                            cursor: "pointer",
                            transition: "all 0.2s",
                        }}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── ONGLET TABLEAU DE BORD ────────────────────────────────── */}
            {activeTab === "dashboard" && (
                <main className="doMain">
                    {/* SECTION 1 — KPI principaux */}
                    <SectionTitle icon={<UserPlus size={18} />}>Indicateurs d'intégration</SectionTitle>
                    <div className="doGrid doGrid--4">
                        <StatCard title="Nouvelles recrues" value={data.kpis.totalNewHires} icon={<UserPlus size={20} />} color={COLORS.primary} />
                        <StatCard title="Onboarding en cours" value={data.kpis.onboardingInProgress} icon={<Clock size={20} />} color={COLORS.warning} />
                        <StatCard title="Onboarding terminés" value={data.kpis.completedOnboarding} icon={<UserCheck size={20} />} color={COLORS.success} trend="+12%" />
                        <StatCard title="Abandons" value={data.kpis.dropouts} icon={<AlertTriangle size={20} />} color={COLORS.error} />
                    </div>

                    <div className="doGrid doGrid--4">
                        <StatCard title="Durée moyenne" value={`${data.kpis.avgCompletionDays}j`} subtitle="Temps moyen d'intégration" icon={<Clock size={20} />} color={COLORS.info} />
                        <StatCard title="Taux de satisfaction" value={`${data.kpis.satisfactionRate}%`} icon={<Star size={20} />} color={COLORS.purple} trend="+3%" />
                        <StatCard title="Taux de complétion" value={`${data.kpis.completionRate}%`} icon={<Target size={20} />} color={COLORS.teal} />
                        <StatCard title="Parrains assignés" value={data.kpis.mentorAssigned} subtitle={`sur ${data.kpis.totalNewHires} recrues`} icon={<BookOpen size={20} />} color={COLORS.primary} />
                    </div>

                    {/* SECTION 2 — Graphiques répartition */}
                    <SectionTitle icon={<BarChart3 size={18} />}>Répartition &amp; tendances</SectionTitle>
                    <div className="doGrid doGrid--2">
                        <ChartCard title="Onboarding par département">
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={data.onboardingByDepartment}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Bar dataKey="value" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <ChartCard title="Répartition par statut">
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={data.onboardingByStatus}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={105}
                                        paddingAngle={3}
                                        dataKey="value"
                                        label={({ name, value }) => `${name}: ${value}`}
                                    >
                                        {data.onboardingByStatus.map((entry, idx) => (
                                            <Cell key={`cell-${idx}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <div className="doGrid doGrid--2">
                        <ChartCard title="Évolution mensuelle des intégrations">
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={data.monthlyTrend}>
                                    <defs>
                                        <linearGradient id="doGradRecrues" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8} />
                                            <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.05} />
                                        </linearGradient>
                                        <linearGradient id="doGradTermines" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.8} />
                                            <stop offset="95%" stopColor={COLORS.success} stopOpacity={0.05} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Area type="monotone" dataKey="recrues" stroke={COLORS.primary} fillOpacity={1} fill="url(#doGradRecrues)" name="Recrues" />
                                    <Area type="monotone" dataKey="termines" stroke={COLORS.success} fillOpacity={1} fill="url(#doGradTermines)" name="Terminés" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <ChartCard title="Dernières recrues en cours d'intégration">
                            <div style={{ padding: "8px 4px", overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                    <thead>
                                    <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                                        <th style={{ textAlign: "left", padding: "8px 10px", color: COLORS.primary, fontWeight: 700, fontSize: 12 }}>Collaborateur</th>
                                        <th style={{ textAlign: "left", padding: "8px 10px", color: COLORS.primary, fontWeight: 700, fontSize: 12 }}>Département</th>
                                        <th style={{ textAlign: "center", padding: "8px 10px", color: COLORS.primary, fontWeight: 700, fontSize: 12 }}>Progression</th>
                                        <th style={{ textAlign: "center", padding: "8px 10px", color: COLORS.primary, fontWeight: 700, fontSize: 12 }}>Statut</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {data.recentOnboardings.map((r, idx) => (
                                        <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                            <td style={{ padding: "10px", fontWeight: 600, color: "#1f2937" }}>{r.name}</td>
                                            <td style={{ padding: "10px", color: "#6b7280" }}>{r.department}</td>
                                            <td style={{ padding: "10px", textAlign: "center" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                                                    <div style={{ width: 80, height: 7, background: "#f3f4f6", borderRadius: 99, overflow: "hidden" }}>
                                                        <div
                                                            style={{
                                                                width: `${r.progress}%`,
                                                                height: "100%",
                                                                borderRadius: 99,
                                                                background: r.progress === 100 ? COLORS.success : r.progress >= 50 ? COLORS.info : COLORS.warning,
                                                            }}
                                                        />
                                                    </div>
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{r.progress}%</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: "10px", textAlign: "center" }}>
                                                    <span
                                                        style={{
                                                            padding: "3px 10px",
                                                            borderRadius: 99,
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            background: r.status === "Terminé" ? `${COLORS.success}18` : `${COLORS.info}18`,
                                                            color: r.status === "Terminé" ? COLORS.success : COLORS.info,
                                                            border: `1px solid ${r.status === "Terminé" ? COLORS.success : COLORS.info}30`,
                                                        }}
                                                    >
                                                        {r.status}
                                                    </span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </ChartCard>

                    </div>

                </main>
            )}

            {/* ── ONGLET ÉTATS ─────────────────────────────────────────── */}
            {activeTab === "etats" && (
                <main className="doMain">
                    <SectionTitle icon={<FileSpreadsheet size={18} />}>États téléchargeables</SectionTitle>
                    <p style={{ color: "#6b7280", fontSize: 13.5, marginBottom: 18 }}>
                        Téléchargez les états de synthèse du module onboarding au format Excel.
                    </p>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                            gap: 18,
                        }}
                    >
                        {STATIC_EXCEL_STATES.map((state) => (
                            <ExcelStateCard
                                key={state.id}
                                title={state.title}
                                description={state.description}
                                color={state.color}
                                onDownload={downloadHandlers[state.id]}
                            />
                        ))}
                    </div>

                </main>
            )}
        </div>
    );
}
